import {
  HackleUser,
  IdentifiersBuilder,
  IdentifierType,
  Properties,
  resolveIdentifiers,
  User
} from "../../../core/internal/model/model"
import { isSameUser, mergeUsers, UserManager } from "../../../core/internal/user/UserManager"
import ObjectUtil from "../../../core/internal/util/ObjectUtil"
import { PropertyOperations, PropertyOperationsBuilder } from "../../property/PropertyOperations"
import { UserCohorts } from "../../../core/internal/user/UserCohort"
import { UserCohortFetcher } from "../../../core/internal/user/UserCohortFetcher"
import { UserTargetFetcher } from "../../../core/internal/user/UserTargetFetcher"
import HacklePropertyGenerator from "../../property/HacklePropertyGenerator"
import Logger from "../../../core/internal/logger"
import { Clock } from "../../../core/internal/util/TimeUtil"
import { CampaignParser } from "../../attribution/CampaignParser"
import { PageManager } from "../../../core/internal/page/PageManager"
import { Updated } from "../../../core/internal/util/Updated"
import { UserTargets } from "../../../core/internal/user/UserTarget"
import { UserStorage } from "./UserStorage"
import { LocalUserContext } from "./LocalUserContext"

/**
 * Local-mode user manager: resolves a HackleUser with on-device cohorts/target events and refreshes
 * them when the user changes. Mutation methods are fused — they apply the change and then run the
 * needed cohort/target sync, resolving when it settles.
 */
export class LocalUserManager extends UserManager {
  private context: LocalUserContext

  constructor(
    private readonly hackleDeviceId: string,
    private readonly storage: UserStorage,
    private readonly userTargetFetcher: UserTargetFetcher,
    private readonly userCohortFetcher: UserCohortFetcher,
    private readonly campaignParser: CampaignParser,
    private readonly pageManager: PageManager,
    private readonly clock: Clock,
    previousUser: User | null,
    initUser: User | null
  ) {
    super()
    this.context = this.newContext(this.defaultUser)
    this.initialize(initUser ?? previousUser ?? null)
  }

  private get defaultUser(): User {
    return { id: this.hackleDeviceId, deviceId: this.hackleDeviceId }
  }

  private newContext(user: User): LocalUserContext {
    return LocalUserContext.of(decorate(user, this.hackleDeviceId), UserCohorts.empty(), UserTargets.empty())
  }

  initialize(user?: User | null): void {
    this.context = this.newContext(user ?? this.defaultUser)
    this.storage.saveUser(this.context.user)
  }

  get currentUser(): User {
    return this.context.user
  }

  // HackleUser resolve (pure — no mutation; identity changes only via setUser/setUserId/setDeviceId/resetUser)

  hackleUser(user?: User | string): HackleUser {
    if (user === undefined) {
      return this.buildHackleUser(this.context)
    }
    const resolved: User = typeof user === "string" ? { id: user } : user
    return this.buildHackleUser(this.context.with(resolved))
  }

  private buildHackleUser(context: LocalUserContext): HackleUser {
    const builder = new IdentifiersBuilder()
      .addIdentifiers(context.user.identifiers || {})
      .add(IdentifierType.ID, context.user.id || this.hackleDeviceId)
      .add(IdentifierType.DEVICE, context.user.deviceId || this.hackleDeviceId)
      .add(IdentifierType.HACKLE_DEVICE, this.hackleDeviceId)

    if (ObjectUtil.isNotNullOrUndefined(context.user.userId)) {
      builder.add(IdentifierType.USER, context.user.userId)
    }
    const identifiers = builder.build()

    const campaign: Properties = this.campaignParser.parse()

    const userProperties: Properties = {
      ...campaign,
      ...context.user.properties
    }

    return {
      identifiers: identifiers,
      properties: userProperties,
      hackleProperties: this.hackleProperties(),
      cohorts: context.cohorts.rawCohorts,
      targetEvents: context.targetEvents.rawEvents
    }
  }

  private hackleProperties(): Properties {
    if (typeof window !== "undefined") {
      return HacklePropertyGenerator.generate(window, this.pageManager.currentPage)
    }
    return {}
  }

  // User update (fused: apply the change, then synchronize if needed)

  setUser(user: User): Promise<void> {
    return this.syncIfNeeded(this.updateUser(user).map((it) => it.user))
  }

  setUserId(userId: string | undefined): Promise<void> {
    return this.setUser({ ...this.context.user, userId })
  }

  setDeviceId(deviceId: string): Promise<void> {
    return this.setUser({ ...this.context.user, deviceId })
  }

  updateUserProperties(operations: PropertyOperations): Promise<void> {
    this.notifyPropertyOperations(this.context.user, operations, this.clock.currentMillis())
    return this.syncIfNeeded(this.operateProperties(operations).map((it) => it.user))
  }

  resetUser(): Promise<void> {
    const updated = this.updateContext(() => this.defaultUser)
    this.notifyPropertyOperations(updated.current.user, clearAllOps(), this.clock.currentMillis())
    return this.syncIfNeeded(updated.map((it) => it.user))
  }

  private updateContext(updater: (user: User) => User): Updated<LocalUserContext> {
    const oldContext = this.context
    const oldUser = oldContext.user
    const newUser = updater(oldUser)

    const newContext = this.context.with(newUser)
    this.context = newContext

    if (!isSameUser(oldUser, newUser)) {
      this.notifyUserUpdated(oldUser, newUser, this.clock.currentMillis())
      this.storage.saveUser(newUser)
    }

    return new Updated<LocalUserContext>(oldContext, newContext)
  }

  private updateUser(user: User): Updated<LocalUserContext> {
    return this.updateContext((currentUser) => mergeUsers(currentUser, decorate(user, this.hackleDeviceId)))
  }

  private operateProperties(operations: PropertyOperations): Updated<LocalUserContext> {
    return this.updateContext((currentUser) => {
      const userProperties = currentUser.properties ?? {}
      const properties = operations.operate(new Map(Object.entries(userProperties)))

      return { ...currentUser, properties: ObjectUtil.fromMap(properties) }
    })
  }

  // Sync

  async sync(): Promise<void> {
    await Promise.all([this.syncCohort(), this.syncUserTarget()])
  }

  /**
   * Refreshes cohorts/target events when the user change requires it.
   *
   * This is intentionally NOT on the `UserManager` contract — sync orchestration is owned by the
   * manager (the fused `setUser`/`setUserId`/`setDeviceId`/`updateUserProperties`/`resetUser` call
   * it internally) rather than by callers. It is `public` rather than `private` only so its branch
   * logic (identifier change → cohort/target) can be unit-tested directly with crafted
   * `Updated<User>` inputs.
   */
  syncIfNeeded(updated: Updated<User>): Promise<void> {
    const tasks: Promise<void>[] = []
    if (this.hasNewIdentifiers(updated.previous, updated.current)) {
      tasks.push(this.syncCohort())
    }

    if (!isSameUser(updated.previous, updated.current)) {
      tasks.push(this.syncUserTarget())
    }

    return Promise.all(tasks).then(() => undefined)
  }

  private async syncCohort() {
    try {
      const cohorts = await this.userCohortFetcher.fetch(this.currentUser)
      this.context = this.context.update(cohorts, this.context.targetEvents)
    } catch (err) {
      Logger.log.error(`Failed to sync cohorts: ${err}`)
    }
  }

  private async syncUserTarget() {
    try {
      const events = await this.userTargetFetcher.fetch(this.currentUser)
      this.context = this.context.update(this.context.cohorts, events)
    } catch (err) {
      Logger.log.error(`Failed to sync userTargets: ${err}`)
    }
  }

  private hasNewIdentifiers(previous: User, current: User): boolean {
    const previousIdentifiers = resolveIdentifiers(previous)
    const currentIdentifiers = resolveIdentifiers(current)
    return Object.entries(currentIdentifiers).some(([type, value]) => previousIdentifiers[type] !== value)
  }

  async close(): Promise<void> {}
}

const decorate = (user: User, hackleDeviceId: string): User => {
  return {
    ...user,
    id: user.id || hackleDeviceId,
    deviceId: user.deviceId || hackleDeviceId
  }
}

const clearAllOps = (): PropertyOperations => {
  return new PropertyOperationsBuilder().clearAll().build()
}
